import React, { useState, useEffect } from 'react';
import { ViberMessageOptions, ViberCarouselItem } from '../types/viber.types';
import MediaUploader from './MediaUploader';
import Icon from './Icon';
import { useT } from '../i18n/LanguageContext';

interface ViberMessageBuilderProps {
  viberContent: ViberMessageOptions;
  onChange: (content: ViberMessageOptions) => void;
}

const ViberMessageBuilder: React.FC<ViberMessageBuilderProps> = ({ viberContent, onChange }) => {
  const t = useT();
  const [activeTab, setActiveTab] = useState<'basic' | 'button' | 'image' | 'video' | 'carousel' | 'advanced'>('basic');
  const [advOpen, setAdvOpen] = useState(false);

  // Section tiles (was the tab bar). `tab` is the same active-section key as before.
  const SECTION_TILES: { tab: 'basic' | 'button' | 'image' | 'video' | 'carousel'; label: string; icon: string }[] = [
    { tab: 'basic', label: t('Tekst', 'Text'), icon: 'type' },
    { tab: 'button', label: t('Dugme', 'Button'), icon: 'zap' },
    { tab: 'image', label: t('Slika', 'Image'), icon: 'image' },
    { tab: 'video', label: t('Video', 'Video'), icon: 'play' },
    { tab: 'carousel', label: t('Carousel', 'Carousel'), icon: 'grid' },
  ];

  const handleContentChange = (updates: Partial<ViberMessageOptions>) => {
    onChange({ ...viberContent, ...updates });
  };

  const addCarouselItem = () => {
    const newItem: ViberCarouselItem = {
      title: 'New Item',
      imageUrl: '',
      primaryButton: {
        text: 'Click',
        action: 'https://',
      },
    };

    const carousel = viberContent.carousel || { items: [] };
    handleContentChange({
      carousel: {
        items: [...carousel.items, newItem],
      },
    });
  };

  const updateCarouselItem = (index: number, updates: Partial<ViberCarouselItem>) => {
    if (!viberContent.carousel) return;

    const items = [...viberContent.carousel.items];
    items[index] = { ...items[index], ...updates };
    handleContentChange({
      carousel: { items },
    });
  };

  const removeCarouselItem = (index: number) => {
    if (!viberContent.carousel) return;

    const items = viberContent.carousel.items.filter((_, i) => i !== index);
    handleContentChange({
      carousel: items.length > 0 ? { items } : undefined,
    });
  };

  const isTx = viberContent.label === 'TRANSACTION';

  // Which content types are currently filled (drives mutual exclusivity).
  const filled = {
    text: !!(viberContent.text && viberContent.text.trim()),
    button: !!viberContent.button,
    image: !!viberContent.image,
    video: !!viberContent.video?.url,
    carousel: !!viberContent.carousel?.items?.length,
  };

  // Human label for the active combination (e.g. "Text+Image+Button").
  const computeActiveType = (): string => {
    if (filled.carousel) return t('Carousel', 'Carousel');
    const parts: string[] = [];
    if (filled.video) parts.push(t('Video', 'Video'));
    if (filled.image) parts.push(t('Slika', 'Image'));
    if (filled.text) parts.push(t('Tekst', 'Text'));
    if (filled.button) parts.push(t('Dugme', 'Button'));
    return parts.join('+') || t('Tekst', 'Text');
  };
  const activeType = computeActiveType();

  // Allowed combos (per NTH): Text, Text+Image, Text+Button, Text+Image+Button,
  // Image, Video, Video+Text, Video+Text+Button, Carousel.
  // => text is always allowed; image/video/carousel are mutually exclusive;
  //    a button is allowed with text/image/video but NOT with a carousel.
  // Transaction messages are text-only.
  const isTabDisabled = (tab: string): boolean => {
    if (isTx && tab !== 'basic' && tab !== 'advanced') return true;
    switch (tab) {
      case 'basic': return false;                      // text always allowed
      case 'button': return filled.carousel;           // button ok with text/image/video, not carousel
      case 'image': return filled.video || filled.carousel;
      case 'video': return filled.image || filled.carousel;
      case 'carousel': return filled.button || filled.image || filled.video;
      default: return false;
    }
  };

  // If the open tab becomes incompatible, jump to the active content's tab.
  useEffect(() => {
    if (isTabDisabled(activeTab)) {
      const next = filled.carousel ? 'carousel' : filled.video ? 'video' : filled.image ? 'image' : filled.button ? 'button' : 'basic';
      setActiveTab(next as any);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viberContent]);

  const removeBtnStyle: React.CSSProperties = {
    marginTop: 15,
    padding: '8px 15px',
    background: 'var(--danger-soft)',
    color: 'var(--danger)',
    border: '1px solid var(--danger)',
    borderRadius: 'var(--r-xs)',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: 13,
  };

  return (
    <div>
      {/* Channel / active type hint */}
      <div className="flex items-center gap-12" style={{ marginBottom: 16 }}>
        <span className="chip chip-channel chip-viber">Viber</span>
        <span className="dim" style={{ fontSize: 13 }}>{t('Rich poruka', 'Rich message')}</span>
      </div>

      {/* Label toggle: Promocija / Transakcija */}
      <div className="field">
        <label className="label">{t('Tip naloga poruke', 'Message account type')}</label>
        <div className="seg">
          <button
            type="button"
            className={!isTx ? 'active' : ''}
            onClick={() => handleContentChange({ label: 'PROMOTION' })}
          >
            {t('Promocija', 'Promotion')}
          </button>
          <button
            type="button"
            className={isTx ? 'active' : ''}
            onClick={() => { handleContentChange({ label: 'TRANSACTION' }); setActiveTab('basic'); }}
          >
            {t('Transakcija', 'Transaction')}
          </button>
        </div>
        <div className="hint">
          {isTx
            ? t('Transakcione poruke podržavaju samo tekst (bez dugmadi, slika, videa).', 'Transaction messages support text only (no buttons, images, videos).')
            : t('Promocija podržava sav rich sadržaj (dugme, slika, video, carousel).', 'Promotion supports all rich content (button, image, video, carousel).')}
        </div>
      </div>

      {/* Active type hint */}
      <div className="hint" style={{ marginTop: -8, marginBottom: 14 }}>
        {t('Tip poruke:', 'Message type:')} <strong style={{ color: 'var(--text-2)' }}>{activeType}</strong> — {t('tekst ide uz sve; slika/video/carousel se međusobno isključuju; dugme zahteva tekst i nije dozvoljeno uz carousel.', 'text goes with everything; image/video/carousel are mutually exclusive; a button requires text and is not allowed with a carousel.')}
        {filled.button && !filled.text && t(' Dugme zahteva tekst — dodaj tekst u Tekst sekciju.', ' A button requires text — add text in the Text section.')}
      </div>

      {/* Message-type tiles (was the tab bar) */}
      <div className="field">
        <label className="label">{t('Tip poruke', 'Message type')}</label>
        <div className="viber-types">
          {SECTION_TILES.map((ty) => {
            const disabled = isTabDisabled(ty.tab);
            const active = activeTab === ty.tab;
            return (
              <button
                key={ty.tab}
                type="button"
                disabled={disabled}
                title={disabled ? t('Nije dostupno uz trenutni tip poruke (ukloni drugi sadržaj da koristiš ovo)', 'Not available with the current message type (remove other content to use this)') : ''}
                onClick={() => { if (!disabled) setActiveTab(ty.tab); }}
                style={{
                  padding: '12px 8px',
                  borderRadius: 'var(--r-md)',
                  border: `1.5px solid ${active ? 'var(--ch-viber)' : 'var(--border)'}`,
                  background: active ? 'var(--ch-viber-soft)' : 'var(--surface)',
                  color: active ? 'var(--ch-viber)' : 'var(--text-2)',
                  opacity: disabled ? 0.4 : 1,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 7,
                  transition: '.15s',
                }}
              >
                <Icon name={ty.icon} size={19} />
                <span style={{ fontSize: 11.5, fontWeight: 700, lineHeight: 1.2, textAlign: 'center' }}>{ty.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Section content */}
      {/* Basic Text */}
      {activeTab === 'basic' && (
        <div>
          <div className="field">
            <label className="label">{t('Tekst poruke', 'Message text')} <span className="dim">(max 1000)</span></label>
            <textarea
              className="textarea"
              style={{ minHeight: 110 }}
              value={viberContent.text || ''}
              onChange={(e) => handleContentChange({ text: e.target.value })}
              placeholder={t('Pozdrav {{name}}! Otkrijte našu novu kolekciju…', 'Hello {{name}}! Discover our new collection…')}
              maxLength={1000}
            />
            <div className="flex items-center justify-between mt-8">
              <span className="hint" style={{ margin: 0 }}>{t('Promenljive:', 'Variables:')} {'{{name}}'}, {'{{phone}}'}, {'{{custom.fieldName}}'}</span>
              <span className="chip"><span className="tnum">{(viberContent.text || '').length}</span> / 1000</span>
            </div>
          </div>

          <div className="card" style={{ padding: 14, background: 'var(--surface-2)' }}>
            <div className="flex items-center gap-8" style={{ fontWeight: 700, fontSize: 13.5, marginBottom: 6 }}>
              <Icon name="info" size={15} /> {t('Promenljive', 'Placeholders')}
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--text-2)' }}>
              <li><code>{'{{name}}'}</code> - {t('Ime primaoca', 'Recipient name')}</li>
              <li><code>{'{{phone}}'}</code> - {t('Broj telefona', 'Phone number')}</li>
              <li><code>{'{{custom.fieldName}}'}</code> - {t('Prilagođena polja', 'Custom fields')}</li>
            </ul>
          </div>
        </div>
      )}

      {/* Button */}
      {activeTab === 'button' && (
        <div>
          <div className="card" style={{ padding: 12, background: 'var(--surface-2)', marginBottom: 16 }}>
            <span className="flex items-center gap-8" style={{ fontSize: 13, color: 'var(--text-2)' }}>
              <Icon name="alert" size={14} /> {t('Dugmad rade samo uz', 'Buttons work only with')} <strong>PROMOTION</strong> {t('poruke i zahtevaju tekst.', 'messages and require text.')}
            </span>
          </div>

          <div className="field">
            <label className="label">{t('Tekst dugmeta', 'Button text')} <span className="dim">(max 30)</span> *</label>
            <input
              className="input"
              type="text"
              value={viberContent.button?.caption || ''}
              onChange={(e) =>
                handleContentChange({
                  button: {
                    caption: e.target.value,
                    action: viberContent.button?.action || 'https://',
                  },
                })
              }
              placeholder={t('Saznaj više', 'Learn more')}
              maxLength={30}
            />
            <div className="flex items-center justify-between mt-8">
              <span className="hint" style={{ margin: 0 }} />
              <span className="chip"><span className="tnum">{(viberContent.button?.caption || '').length}</span> / 30</span>
            </div>
          </div>

          <div className="field">
            <label className="label">{t('Link akcije (URL)', 'Action link (URL)')} *</label>
            <input
              className="input"
              type="url"
              value={viberContent.button?.action || ''}
              onChange={(e) =>
                handleContentChange({
                  button: {
                    caption: viberContent.button?.caption || '',
                    action: e.target.value,
                  },
                })
              }
              placeholder="https://shop.com/products?ref={{custom.customerId}}"
            />
          </div>

          <button
            type="button"
            onClick={() => handleContentChange({ button: undefined })}
            style={removeBtnStyle}
          >
            {t('Ukloni dugme', 'Remove button')}
          </button>
        </div>
      )}

      {/* Image */}
      {activeTab === 'image' && (
        <div>
          <div className="card" style={{ padding: 12, background: 'var(--surface-2)', marginBottom: 16 }}>
            <span className="flex items-center gap-8" style={{ fontSize: 13, color: 'var(--text-2)' }}>
              <Icon name="info" size={14} /> {t('Slike rade samo uz', 'Images work only with')} <strong>PROMOTION</strong> {t('poruke. Formati: PNG, JPEG, JPG', 'messages. Formats: PNG, JPEG, JPG')}
            </span>
          </div>

          <div className="field">
            <label className="label">{t('Slika (URL)', 'Image (URL)')} *</label>
            <input
              className="input"
              type="url"
              value={viberContent.image || ''}
              onChange={(e) => handleContentChange({ image: e.target.value })}
              placeholder="https://cdn.shop.com/products/image.jpg"
            />
          </div>

          <MediaUploader
            accept="image"
            onSelect={(f) => handleContentChange({ image: f.url })}
          />

          {viberContent.image && (
            <div style={{ marginTop: 15 }}>
              <img
                src={viberContent.image}
                alt={t('Pregled', 'Preview')}
                style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 'var(--r-sm)' }}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
          )}

          <div>
            <button
              type="button"
              onClick={() => handleContentChange({ image: undefined })}
              style={removeBtnStyle}
            >
              {t('Ukloni sliku', 'Remove image')}
            </button>
          </div>
        </div>
      )}

      {/* Video */}
      {activeTab === 'video' && (
        <div>
          <div className="card" style={{ padding: 12, background: 'var(--surface-2)', marginBottom: 16 }}>
            <span className="flex items-center gap-8" style={{ fontSize: 13, color: 'var(--text-2)' }}>
              <Icon name="play" size={14} /> {t('Videi rade samo uz', 'Videos work only with')} <strong>PROMOTION</strong> {t('poruke. Max 600s, Max 200MB', 'messages. Max 600s, Max 200MB')}
            </span>
          </div>

          <div className="field">
            <label className="label">{t('Video (URL)', 'Video (URL)')} *</label>
            <input
              className="input"
              type="url"
              value={viberContent.video?.url || ''}
              onChange={(e) =>
                handleContentChange({
                  video: {
                    url: e.target.value,
                    duration: viberContent.video?.duration || 60,
                    fileSize: viberContent.video?.fileSize || 10,
                    thumbnail: viberContent.video?.thumbnail || '',
                  },
                })
              }
              placeholder="https://cdn.shop.com/videos/promo.mp4"
            />
          </div>

          <MediaUploader
            accept="video"
            onSelect={(f) =>
              handleContentChange({
                video: {
                  url: f.url,
                  duration: viberContent.video?.duration || 60,
                  // bytes -> MB (rounded up), capped for display
                  fileSize: Math.max(1, Math.ceil(f.size / (1024 * 1024))),
                  thumbnail: viberContent.video?.thumbnail || '',
                },
              })
            }
          />

          <div className="grid-2" style={{ marginTop: 16 }}>
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="label">{t('Trajanje (s)', 'Duration (s)')} *</label>
              <input
                className="input"
                type="number"
                min="1"
                max="600"
                value={viberContent.video?.duration || ''}
                onChange={(e) =>
                  handleContentChange({
                    video: {
                      url: viberContent.video?.url || '',
                      duration: parseInt(e.target.value),
                      fileSize: viberContent.video?.fileSize || 10,
                      thumbnail: viberContent.video?.thumbnail || '',
                    },
                  })
                }
              />
            </div>

            <div className="field" style={{ marginBottom: 0 }}>
              <label className="label">{t('Veličina (MB)', 'Size (MB)')} *</label>
              <input
                className="input"
                type="number"
                min="1"
                max="200"
                value={viberContent.video?.fileSize || ''}
                onChange={(e) =>
                  handleContentChange({
                    video: {
                      url: viberContent.video?.url || '',
                      duration: viberContent.video?.duration || 60,
                      fileSize: parseInt(e.target.value),
                      thumbnail: viberContent.video?.thumbnail || '',
                    },
                  })
                }
              />
            </div>
          </div>

          <div className="field" style={{ marginTop: 16 }}>
            <label className="label">{t('Thumbnail (URL)', 'Thumbnail (URL)')} *</label>
            <input
              className="input"
              type="url"
              value={viberContent.video?.thumbnail || ''}
              onChange={(e) =>
                handleContentChange({
                  video: {
                    url: viberContent.video?.url || '',
                    duration: viberContent.video?.duration || 60,
                    fileSize: viberContent.video?.fileSize || 10,
                    thumbnail: e.target.value,
                  },
                })
              }
              placeholder="https://cdn.shop.com/thumbnails/promo-thumb.jpg"
            />
          </div>

          <button
            type="button"
            onClick={() => handleContentChange({ video: undefined })}
            style={removeBtnStyle}
          >
            {t('Ukloni video', 'Remove video')}
          </button>
        </div>
      )}

      {/* Carousel */}
      {activeTab === 'carousel' && (
        <div>
          <div className="card" style={{ padding: 12, background: 'var(--surface-2)', marginBottom: 16 }}>
            <span className="flex items-center gap-8" style={{ fontSize: 13, color: 'var(--text-2)' }}>
              <Icon name="layout" size={14} /> {t('Carousel mora imati 2-5 kartica. Svaka kartica zahteva naslov (2-38 znakova), sliku i primarno dugme.', 'A carousel must have 2-5 cards. Each card requires a title (2-38 characters), an image, and a primary button.')}
            </span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {viberContent.carousel?.items.map((item, index) => (
              <div
                key={index}
                className="card"
                style={{ padding: 14, background: 'var(--surface-2)' }}
              >
                <div className="flex items-center justify-between" style={{ marginBottom: 10 }}>
                  <strong style={{ fontSize: 14 }}>{t('Kartica', 'Card')} {index + 1}</strong>
                  <button
                    type="button"
                    className="icon-btn"
                    onClick={() => removeCarouselItem(index)}
                    style={{ color: 'var(--danger)' }}
                    title={t('Ukloni', 'Remove')}
                  >
                    <Icon name="trash" size={16} />
                  </button>
                </div>

                <div className="field" style={{ marginBottom: 10 }}>
                  <label className="label">{t('Naslov (2-38 znakova)', 'Title (2-38 characters)')}</label>
                  <input
                    className="input"
                    type="text"
                    value={item.title}
                    onChange={(e) => updateCarouselItem(index, { title: e.target.value })}
                    minLength={2}
                    maxLength={38}
                  />
                </div>

                <div className="field" style={{ marginBottom: 10 }}>
                  <label className="label">{t('Slika (URL)', 'Image (URL)')}</label>
                  <input
                    className="input"
                    type="url"
                    value={item.imageUrl}
                    onChange={(e) => updateCarouselItem(index, { imageUrl: e.target.value })}
                  />
                </div>

                <div className="grid-2">
                  <div className="field" style={{ marginBottom: 0 }}>
                    <label className="label">{t('Tekst primarnog dugmeta', 'Primary button text')}</label>
                    <input
                      className="input"
                      type="text"
                      value={item.primaryButton.text}
                      onChange={(e) =>
                        updateCarouselItem(index, {
                          primaryButton: { ...item.primaryButton, text: e.target.value },
                        })
                      }
                    />
                  </div>

                  <div className="field" style={{ marginBottom: 0 }}>
                    <label className="label">{t('Akcija primarnog dugmeta', 'Primary button action')}</label>
                    <input
                      className="input"
                      type="url"
                      value={item.primaryButton.action}
                      onChange={(e) =>
                        updateCarouselItem(index, {
                          primaryButton: { ...item.primaryButton, action: e.target.value },
                        })
                      }
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={addCarouselItem}
            disabled={(viberContent.carousel?.items.length || 0) >= 5}
            style={{ marginTop: 14 }}
          >
            <Icon name="plus" size={14} /> {t('Dodaj karticu', 'Add card')} ({viberContent.carousel?.items.length || 0}/5)
          </button>
        </div>
      )}

      {/* Advanced options (tag, TTL, smartphoneOnly) */}
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        style={{ marginTop: 14, paddingLeft: 4 }}
        onClick={() => setAdvOpen(!advOpen)}
      >
        <Icon name={advOpen ? 'chevronDown' : 'chevronRight'} size={15} /> {t('Napredne opcije', 'Advanced options')}
      </button>

      {advOpen && (
        <div className="card" style={{ padding: 16, marginTop: 8, background: 'var(--surface-2)' }}>
          <div className="grid-2">
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="label">{t('Tag (analitika)', 'Tag (analytics)')}</label>
              <input
                className="input"
                type="text"
                value={viberContent.tag || ''}
                onChange={(e) => handleContentChange({ tag: e.target.value })}
                placeholder="leto-2026"
                maxLength={100}
              />
            </div>

            <div className="field" style={{ marginBottom: 0 }}>
              <label className="label">{t('TTL — istek (s)', 'TTL — expiry (s)')}</label>
              <input
                className="input"
                type="number"
                value={viberContent.ttl || 86400}
                onChange={(e) => handleContentChange({ ttl: parseInt(e.target.value) })}
                min={30}
                max={1209600}
              />
              <div className="hint">{t('Podrazumevano: 86400 (24h), Min: 30, Max: 1209600 (14 dana)', 'Default: 86400 (24h), Min: 30, Max: 1209600 (14 days)')}</div>
            </div>
          </div>

          <label className="flex items-center gap-8" style={{ marginTop: 14, fontSize: 13.5, fontWeight: 600, cursor: 'pointer' }}>
            <button
              type="button"
              className={`switch${viberContent.smartphoneOnly ? ' on' : ''}`}
              onClick={() => handleContentChange({ smartphoneOnly: !viberContent.smartphoneOnly })}
              aria-pressed={!!viberContent.smartphoneOnly}
            />
            {t('Samo pametni telefoni (bez tableta)', 'Smartphones only (no tablets)')}
          </label>
        </div>
      )}
    </div>
  );
};

export default ViberMessageBuilder;
